import { ActionUnion, ContractUnion } from "../../types";
import locale from "@bonliva-crm/locale";
import {
  Color,
  EditIcon,
  CloudUploadIcon,
  AssignmentTurnedInIcon,
  ContentCopyIcon,
  OpenInNewIcon,
  DownloadIcon,
  DeleteIcon,
  SendIcon,
  NoteAddIcon,
  CancelIcon,
  ResetIcon,
  SelectCheckBoxIcon
} from "@bonliva-crm/ui";
import { isConfirmation, isFrameworkOrIntermittent, isSalaryChoiceError } from "./utils";
import moment from "moment";
import { ConsultantBookingContractType } from "@bonliva-crm/graphql";
import { getConfirmationContractType } from "@consultants/ConsultantContract/utils";
import { ConsultantContractType } from "@bonliva-crm/shared";
import { getConsultantAgeGroup } from "@bonliva-crm/shared/consultant-contract-strategy/utils";

type EntryProps = {
  closeList: () => void;
  setAction: (a: string) => void;
  contract: ContractUnion;
  showError: (a: string) => void;
  handleNavigationAction: (a: ActionUnion, contractId?: string) => void;
  customerContract?: boolean;
  disabledDelete?: boolean;
};

type Entry = {
  text: string;
  icon: JSX.Element;
  action: () => void;
  disabled?: boolean;
};

const edit = (props: EntryProps) => ({
  text: locale.Edit,
  icon: <EditIcon blank htmlColor={Color.Body} />,
  action: () => {
    props.closeList();
    props.handleNavigationAction(locale.Edit, props.contract._id);
  },
  disabled: !props.contract.canEdit
});

const openInNewWindow = (props: EntryProps) => ({
  text: locale.OpenInNewWindow,
  icon: <OpenInNewIcon />,
  action: () => {
    props.closeList();
    window.open(props.contract.ViewUrl, "_blank");
  },
  disabled: !props.contract.ViewUrl
});

const uploadSignedDocument = (props: EntryProps) => ({
  text: locale.UploadSignedDocument,
  icon: <CloudUploadIcon />,
  action: () => {
    props.closeList();
    props.setAction(locale.UploadSignedDocument);
  },
  disabled: props.contract.IsSigned
});

const sendToOneflow = (props: EntryProps) => ({
  text:
    "PublishedUrl" in props.contract && props.contract.PublishedUrl
      ? locale.RepublishToOneflow
      : locale.SendToOneflow,
  icon: <SendIcon blank />,
  action: () => {
    props.closeList();
    props.setAction(
      "PublishedUrl" in props.contract && props.contract.PublishedUrl
        ? locale.RepublishToOneflow
        : locale.SendToOneflow
    );
  },
  disabled: props.contract.IsSigned || props.contract.IsNullified
});

const openInOneflow = (props: EntryProps) =>
  "PublishedUrl" in props.contract && {
    text: "Öppna i Oneflow",
    icon: <OpenInNewIcon />,
    action: () => {
      if ("PublishedUrl" in props.contract && props.contract.PublishedUrl) {
        props.closeList();
        window.open(props.contract.PublishedUrl, "_blank");
      }
    },
    disabled: !props.contract.PublishedUrl
  };

const sendToConsultant = (props: EntryProps) => ({
  text: locale.SendToConsultant,
  icon: <SendIcon blank />,
  action: () => {
    props.closeList();
    props.setAction(locale.SendToConsultant);
  }
});
const sendToCustomer = (props: EntryProps) => ({
  text: locale.SendToCustomer,
  icon: <SendIcon blank />,
  action: () => {
    props.closeList();
    props.setAction(locale.SendToCustomer);
  }
});

const approvedByConsultant = (props: EntryProps) => ({
  text: locale.ApprovedByConsultant,
  icon: <SelectCheckBoxIcon />,
  action: () => {
    props.closeList();
    props.setAction(locale.ApprovedByConsultant);
  },
  disabled: props.contract.IsSigned
});

const addSalaryChoice = (props: EntryProps) => ({
  text: locale.AddSalaryChoice,
  icon: <NoteAddIcon />,
  action: () => {
    props.closeList();
    props.setAction(locale.AddSalaryChoice);
  }
});

const nullify = (props: EntryProps) => ({
  text: locale.Nullify,
  icon: <CancelIcon />,
  action: () => {
    props.closeList();
    props.setAction(locale.Nullify);
  }
});

const unNullify = (props: EntryProps) => ({
  text: locale.UnNullify,
  icon: <ResetIcon />,
  action: () => {
    props.closeList();
    props.setAction(locale.UnNullify);
  }
});

const addBookingConfirm = (props: EntryProps) => ({
  text: `${locale.Add} ${
    locale.consultantContractTypes[
      getConfirmationContractType(props.contract.Type as ConsultantContractType)
    ]
  }`,
  icon: <AssignmentTurnedInIcon />,
  action: () => {
    props.closeList();
    console.log(props.contract.Type);
    props.handleNavigationAction(locale.AddBookingConfirm, props.contract._id);
  },
  disabled: !props.contract.IsSigned
});

const duplicate = (props: EntryProps) => ({
  text: locale.Duplicate,
  icon: <ContentCopyIcon blank htmlColor={Color.Body} />,
  action: () => {
    props.closeList();
    props.handleNavigationAction(locale.Duplicate, props.contract._id);
  },
  disabled: !props.contract.canDuplicate
});

const download = (props: EntryProps) => ({
  text: locale.Download,
  icon: <DownloadIcon />,
  action: () => {
    props.closeList();
    window.open(props.contract.FileUrl, "_blank");
  },
  disabled: !props.contract.canDownload
});

const deleteEntry = (props: EntryProps, disabled?: boolean) => {
  return {
    text: locale.Delete,
    icon: <DeleteIcon />,
    action: () => {
      props.closeList();
      props.setAction(locale.Delete);
    },
    disabled
  };
};

const consultantContract = (props: EntryProps) => {
  const contractType = props.contract.Type;

  const entries: Entry[] = [edit(props), duplicate(props), download(props)];

  if (props.contract.canPreview) {
    entries.push(openInNewWindow(props));
  }

  if ("PublishedUrl" in props.contract && props.contract.PublishedUrl) {
    entries.push(openInOneflow(props));
  }

  if (!isConfirmation(contractType)) {
    entries.push(uploadSignedDocument(props));
  }

  if (
    "canPublishToOneflowDirectly" in props.contract &&
    props.contract.canPublishToOneflowDirectly
  ) {
    entries.push(sendToOneflow(props));
  }

  if (isConfirmation(contractType)) {
    entries.push(sendToConsultant(props));
  }

  if (isFrameworkOrIntermittent(contractType)) {
    entries.push(addBookingConfirm(props));
  }

  if ("Consultant" in props.contract) {
    const { is25To65 } = getConsultantAgeGroup(
      moment(props.contract.Consultant.BirthDate).format("YYYY-MM-DD"),
      moment(props.contract.StartDate).format("YYYY-MM-DD"),
      moment(props.contract.EndDate).format("YYYY-MM-DD")
    );

    if (isSalaryChoiceError(props.contract, is25To65)) {
      entries.push(addSalaryChoice(props));
    }
  }

  if (isConfirmation(contractType)) {
    entries.push(approvedByConsultant(props));
  }

  if (!props.customerContract && props.contract.IsSigned && !props.contract.IsNullified) {
    entries.push(nullify(props));
  }

  if (!props.customerContract && props.contract.IsSigned && props.contract.IsNullified) {
    entries.push(unNullify(props));
  }
  entries.push(deleteEntry(props));

  return entries;
};

const customerContract = (props: EntryProps) => {
  const contractType = props.contract.Type;

  const entries: Entry[] = [
    edit(props),
    duplicate(props),
    download(props),
    openInNewWindow(props)
  ];

  if ("PublishedUrl" in props.contract && props.contract.PublishedUrl) {
    entries.push(openInOneflow(props));
  }

  if (!isConfirmation(contractType)) {
    entries.push(uploadSignedDocument(props));
  }

  if (contractType !== ConsultantBookingContractType.Confirmation) {
    entries.push(sendToOneflow(props));
  }

  if (isConfirmation(contractType)) {
    entries.push(sendToCustomer(props));
  }

  if (props.contract.IsSigned && props.contract.IsNullified) {
    entries.push(unNullify(props));
  }

  if (props.contract.IsSigned && !props.contract.IsNullified) {
    entries.push(nullify(props));
  }

  entries.push(deleteEntry(props, props.disabledDelete));

  return entries;
};

export const entries = (props: EntryProps) => {
  return props.customerContract ? customerContract(props) : consultantContract(props);
};
